/**
 * 비동기 함수 결과를 메모리에 캐시해 같은 인자로 반복 호출 시 네트워크/계산을 줄인다.
 * 동일 키로 동시에 여러 번 호출되면 한 번만 실행하고(in-flight 공유) 나머지는 같은 Promise를 await한다.
 *
 * 사용 예:
 * ```js
 * const fetchByParent = wrapAsyncWithCache(
 *   async (parentId) => {
 *     const res = await BaseApi.get(`/things?parent=${parentId}`);
 *     return handleData(res);
 *   },
 *   {
 *     cacheKey: (parentId) =>
 *       parentId == null || parentId === '' ? null : String(parentId),
 *     maxEntries: 64,
 *     cloneResult: shallowCloneJsonLike,
 *   }
 * );
 * ```
 *
 * @template T
 * @param {(...args: any[]) => Promise<T>} fn - 원본 비동기 함수
 * @param {object} [options]
 * @param {(this: any, ...args: any[]) => string | null | undefined} [options.cacheKey]
 *   캐시 키. `null` / `undefined` 를 반환하면 캐시를 쓰지 않고 매번 `fn` 을 호출한다.
 * @param {number} [options.maxEntries=64] - 서로 다른 키 개수 상한. 넘치면 가장 먼저 들어온 키를 지운다(Map 삽입 순서).
 * @param {(value: T) => T} [options.cloneResult]
 *   히트 시 반환값을 복사할 때(호출부가 객체를 mutate 해도 캐시가 오염되지 않게).
 * @returns {((...args: any[]) => Promise<T>) & {
 *   invalidateCache: (key: string) => void,
 *   clearCache: () => void,
 *   setCachedResult: (key: string | number, value: T) => void
 * }}
 */
export function wrapAsyncWithCache(fn, options = {}) {
  const {
    cacheKey = (...args) => JSON.stringify(args),
    maxEntries = 64,
    cloneResult
  } = options;

  /** @type {Map<string, any>} */
  const settled = new Map();
  /** @type {Map<string, Promise<any>>} */
  const inflight = new Map();

  function evictIfNeeded(newKey) {
    if (settled.size >= maxEntries && !settled.has(newKey)) {
      const oldest = settled.keys().next().value;
      settled.delete(oldest);
    }
  }

  /** @param {any} value */
  function deliver(value) {
    return cloneResult ? cloneResult(value) : value;
  }

  async function wrapped(...args) {
    const key = cacheKey.apply(this, args);
    if (key == null) {
      return fn.apply(this, args);
    }

    const keyStr = String(key);

    if (settled.has(keyStr)) {
      return deliver(settled.get(keyStr));
    }

    if (inflight.has(keyStr)) {
      const result = await inflight.get(keyStr);
      return deliver(result);
    }

    const p = (async () => {
      const result = await fn.apply(this, args);
      evictIfNeeded(keyStr);
      settled.set(keyStr, result);
      return result;
    })();

    inflight.set(keyStr, p);
    try {
      const result = await p;
      return deliver(result);
    } finally {
      inflight.delete(keyStr);
    }
  }

  wrapped.invalidateCache = (key) => {
    settled.delete(String(key));
    inflight.delete(String(key));
  };

  wrapped.clearCache = () => {
    settled.clear();
    inflight.clear();
  };

  /**
   * 서버 저장 직후 등으로 캐시를 수동 갱신할 때 (GET 없이 최신 값 반영).
   * @param {string | number} key — `cacheKey`와 동일한 문자열 규칙(보통 `String(...)`).
   * @param {T} value
   */
  wrapped.setCachedResult = (key, value) => {
    const keyStr = String(key);
    evictIfNeeded(keyStr);
    settled.set(keyStr, value);
  };

  return wrapped;
}

/**
 * 배열(객체 요소)·단일 객체에 대해 1단계 얕은 복사. API로 받은 HAL/목록 캐시 반환에 적합하다.
 * @param {any} value
 * @returns {any}
 */
export function shallowCloneJsonLike(value) {
  if (value == null) return value;
  if (Array.isArray(value)) {
    return value.map((item) => (item && typeof item === 'object' ? { ...item } : item));
  }
  if (typeof value === 'object') {
    return { ...value };
  }
  return value;
}
